import { useCallback, useEffect, useState } from 'react';

import type { SizeSort, SizeSortKey, TypicalSize } from '../types/sizes';

const DEBOUNCE_MS = 400;

export type PerPage = number;

export function useSizesTable() {
    const [queryInput, setQueryInput] = useState('');
    const [appliedQuery, setAppliedQuery] = useState('');
    const [page, setPage] = useState(1);
    const [perPage, setPerPageValue] = useState<PerPage>(75);
    const [sort, setSort] = useState<SizeSort>({ key: 'name', direction: 'asc' });

    useEffect(() => {
        if (queryInput === appliedQuery) return;

        const timer = setTimeout(() => {
            setAppliedQuery(queryInput);
            setPage(1);
        }, DEBOUNCE_MS);

        return () => clearTimeout(timer);
    }, [queryInput]);

    const toggleSort = useCallback((key: SizeSortKey) => {
        setSort((current) =>
            current.key === key
                ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
                : { key, direction: key === 'name' ? 'asc' : 'desc' },
        );
    }, []);

    const setPerPage = useCallback((value: PerPage) => {
        setPerPageValue(value);
        setPage(1);
    }, []);

    const reset = useCallback(() => {
        setQueryInput('');
        setAppliedQuery('');
        setPage(1);
    }, []);

    return {
        query: queryInput,
        setQuery: setQueryInput,
        appliedQuery,
        isFiltered: appliedQuery.trim() !== '',
        reset,
        page,
        setPage,
        perPage,
        setPerPage,
        sort,
        toggleSort,
    };
}

export function sortRows(rows: TypicalSize[], sort: SizeSort): TypicalSize[] {
    const factor = sort.direction === 'asc' ? 1 : -1;

    return rows.slice().sort((a, b) => {
        if (sort.key === 'name') {
            return a.name.localeCompare(b.name, 'ru') * factor;
        }

        if (sort.key === 'createdAt') {
            return (new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()) * factor;
        }

        const left = a[sort.key];
        const right = b[sort.key];

        if (left === null && right === null) return 0;
        if (left === null) return 1;
        if (right === null) return -1;

        return (left - right) * factor;
    });
}

export function useQueueFilter() {
    const [queryInput, setQueryInput] = useState('');
    const [appliedQuery, setAppliedQuery] = useState('');
    const [page, setPage] = useState(1);

    useEffect(() => {
        if (queryInput === appliedQuery) return;

        const timer = setTimeout(() => {
            setAppliedQuery(queryInput);
            setPage(1);
        }, DEBOUNCE_MS);

        return () => clearTimeout(timer);
    }, [queryInput]);

    return {
        query: queryInput,
        setQuery: setQueryInput,
        appliedQuery,
        page,
        setPage,
    };
}